import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { OutageTimeSeriesResponse, CommunityTimeSeriesResponse } from '@/types/outage';
import type { EnergyTimeSeriesResponse } from '@/types/energy';
import type { WeatherAverageResponse } from '@/types/weather';

// Historical data doesn't change - no need to poll. Data is cached by query key (date range).
// When user changes date range, new query key triggers fresh fetch automatically.

export function useOutageHistory(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['outages', 'timeseries', startDate, endDate],
    queryFn: async (): Promise<OutageTimeSeriesResponse> => {
      const params = new URLSearchParams({
        start: startDate,
        end: endDate,
      });
      const res = await fetch(`/api/outages/timeseries?${params}`);
      if (!res.ok) throw new Error('Failed to fetch outage history');
      return res.json();
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    enabled: Boolean(startDate && endDate),
    placeholderData: keepPreviousData,
  });
}

export function useEnergyHistory(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['energy', 'timeseries', startDate, endDate],
    queryFn: async (): Promise<EnergyTimeSeriesResponse> => {
      const params = new URLSearchParams({
        start: startDate,
        end: endDate,
      });
      const res = await fetch(`/api/energy/timeseries?${params}`);
      if (!res.ok) throw new Error('Failed to fetch energy history');
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(startDate && endDate),
    placeholderData: keepPreviousData,
  });
}

export function useCommunityHistory(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['communities', 'timeseries', startDate, endDate],
    queryFn: async (): Promise<CommunityTimeSeriesResponse> => {
      const params = new URLSearchParams({
        start: startDate,
        end: endDate,
      });
      const res = await fetch(`/api/communities/timeseries?${params}`);
      if (!res.ok) throw new Error('Failed to fetch community history');
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(startDate && endDate),
    placeholderData: keepPreviousData,
  });
}

export function useWeatherHistory(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['weather', 'timeseries', startDate, endDate],
    queryFn: async (): Promise<WeatherAverageResponse> => {
      const params = new URLSearchParams({
        start: startDate,
        end: endDate,
      });
      const res = await fetch(`/api/weather/timeseries/averages?${params}`);
      if (!res.ok) return { data: [], meta: { granularity: '1h' as const, rawPointCount: 0, returnedPointCount: 0, algorithm: 'none' as const } };
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(startDate && endDate),
    placeholderData: keepPreviousData,
  });
}

// Hook that returns true if any historical data is currently being fetched
export function useHistoricalFetching(startDate: string, endDate: string) {
  const outages = useOutageHistory(startDate, endDate);
  const energy = useEnergyHistory(startDate, endDate);
  const community = useCommunityHistory(startDate, endDate);
  const weather = useWeatherHistory(startDate, endDate);

  return outages.isFetching || energy.isFetching || community.isFetching || weather.isFetching;
}
